import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Header";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { api } from "@/lib/api";
import thgShield from "@/assets/thg-shield.jpg";

const COLS = [
  {
    title: "GameDay",
    links: [
      { to: "/gameday", label: "GameDay Home" },
      { to: "/nfl", label: "NFL" },
      { to: "/mlb", label: "MLB" },
      { to: "/nhl", label: "NHL" },
      { to: "/us-open-tennis", label: "US Open Tennis" },
    ],
  },
  {
    title: "Explore",
    links: [
      { to: "/venues/metlife-stadium", label: "MetLife Stadium" },
      { to: "/venues/yankee-stadium", label: "Yankee Stadium" },
      { to: "/venues/citi-field", label: "Citi Field" },
      { to: "/venues/ubs-arena", label: "UBS Arena" },
      { to: "/destinations/new-york-city", label: "New York City" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/contact", label: "Contact" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms of Use" },
      { to: "/affiliate-disclosure", label: "Affiliate Disclosure" },
    ],
  },
];

export function Footer() {
  const [cfg, setCfg] = useState({ current_year: new Date().getFullYear(), thg_logo_url: "", thg_website_url: "" });
  useEffect(() => {
    api.get("/config").then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);
  const year = cfg.current_year || new Date().getFullYear();
  const logoSrc = cfg.thg_logo_url || thgShield;

  const shieldImg = (
    <span className="inline-grid place-items-center h-11 w-11 rounded-lg bg-white/95 p-1 shrink-0">
      <img src={logoSrc} alt="Titan Haven Group LLC" className="h-full w-full object-contain" />
    </span>
  );
  const shield = cfg.thg_website_url ? (
    <a href={cfg.thg_website_url} target="_blank" rel="noopener noreferrer" aria-label="Titan Haven Group LLC" data-testid="thg-shield-link">{shieldImg}</a>
  ) : (
    <span data-testid="thg-shield" aria-label="Titan Haven Group LLC">{shieldImg}</span>
  );

  return (
    <footer className="bg-ink text-white/80 mt-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_2fr]">
          <div>
            <Logo light />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
              Travel for the event. Stay for the experience. Hotelstrips helps you figure out
              where to stay, how to get there, and what works best around the events you love.
            </p>
            <div className="mt-6">
              <div className="overline text-white/50 mb-3">Get the GameDay newsletter</div>
              <NewsletterSignup dark />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            {COLS.map((c) => (
              <div key={c.title}>
                <div className="overline text-white/50 mb-4">{c.title}</div>
                <ul className="space-y-3">
                  {c.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="text-sm text-white/70 hover:text-primary transition-colors">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col gap-6">
          <p className="text-xs text-white/50 max-w-3xl leading-relaxed">
            Hotelstrips.com is owned and operated by Titan Haven Group LLC. Hotelstrips may earn
            compensation when you book through certain links.{" "}
            <Link to="/affiliate-disclosure" className="underline hover:text-primary">Affiliate disclosure</Link>.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="flex items-center gap-3">
              {shield}
              <div>
                <div className="overline text-white/40">A Titan Haven Group Experience</div>
                <p className="text-xs text-white/40 mt-1">© {year} Titan Haven Group LLC. All rights reserved.</p>
              </div>
            </div>
            <p className="text-xs text-white/30 shrink-0">Independent travel recommendations. We may earn a commission on bookings.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
